import type { Session } from '@sentra/contracts';
import type { i18n as I18n } from 'i18next';
import type { ComponentProps, ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router';

import type { AppConfig } from '@/app/providers/config';
import { AppConfigProvider } from '@/app/providers/config';
import { QueryProvider } from '@/app/providers/query';
import { SessionCacheBridge } from '@/app/providers/session-cache';
import type { ThemePreference } from '@/app/providers/theme';
import { ThemeProvider } from '@/app/providers/theme';
import { Toaster } from '@/design/components/toaster';
import { LoginPage } from '@/features/auth/login';
import { PasswordChangePage } from '@/features/auth/password-change';
import { PinSetupPage } from '@/features/auth/pin-setup';
import { ReauthenticationOverlay } from '@/features/auth/reauth-overlay';
import { SessionProvider, useSessionContext } from '@/features/auth/session';
import { TotpEnrolmentPage } from '@/features/auth/totp-enrolment';

/**
 * Whatever `RouterProvider` accepts: a history router in a browser, a memory router in a test.
 *
 * Read off the component rather than named, so an upgrade that changes the type is a compile error
 * here instead of a cast somewhere else.
 */
export type AppRouter = ComponentProps<typeof RouterProvider>['router'];

/**
 * The whole application, from the configuration down.
 *
 * **The provider order is not arbitrary.** The configuration is outermost because everything below
 * it, including the timezone the formatters use and the mode the screens branch on, is derived from
 * it and it is already loaded by the time this renders. The theme is next because it writes a class
 * on the document and has no other dependency. Then language, then the session — which builds the
 * API client, and therefore needs the language for `Accept-Language` — and the query cache inside
 * that, because every query goes through the client. That last nesting is also what makes
 * {@link SessionCacheBridge} possible without reordering anything: a component rendered inside the
 * query cache is inside the session too, so it can hold both.
 *
 * **The router is a parameter.** A browser gets a history router and a test gets a memory router,
 * from one route table (`app/routes.tsx`); building a second table for tests would be a second
 * answer to what screens this product has.
 */
export function App({
  config,
  i18n,
  themePreference,
  fetchImpl,
  router,
  initialSession,
}: Readonly<{
  config: AppConfig;
  i18n: I18n;
  themePreference: ThemePreference;
  fetchImpl: typeof globalThis.fetch;
  router: AppRouter;
  initialSession: Session | null;
}>): ReactNode {
  return (
    <AppConfigProvider config={config}>
      <ThemeProvider initialPreference={themePreference}>
        <I18nextProvider i18n={i18n}>
          <SessionProvider fetchImpl={fetchImpl} initialSession={initialSession}>
            <QueryProvider>
              {/*
                Inside the query cache and below the session, which is the only place that can read
                both. It renders nothing; what it does is notice that the operator changed or came
                back from the lock, and tell the cache. See `providers/session-cache.tsx`.
              */}
              <SessionCacheBridge />
              <AuthenticatedArea router={router} />
            </QueryProvider>
            {/*
              Outside the router and outside the shell, deliberately. Signing out unmounts the
              shell, and a toast surface inside it would take the message about a sign-out that the
              server did not confirm down with it — the one moment somebody most needs to read it.
            */}
            <Toaster />
          </SessionProvider>
        </I18nextProvider>
      </ThemeProvider>
    </AppConfigProvider>
  );
}

/**
 * What the terminal shows, given what the session is behind.
 *
 * **The order is the server's own** (`auth/session.ts`): expiry, then the idle lock, then the
 * password change, then the enrolment. Somebody who owes both a password change and an enrolment is
 * sent to the password first, because the password is the credential the enrolment protects.
 *
 * **Only the first two are overlays.** The lock and an expired session appear OVER the router, so
 * the operator's half-filled form is still mounted when they come back. A password change and an
 * enrolment can only be met at sign-in, where there is nothing underneath to preserve, so they are
 * screens — and a modal over an empty application would be a modal with nothing behind it.
 *
 * **The PIN step is this application's, not the server's.** The server does not refuse a session
 * without a PIN; `PinSetupPage` says why it is asked for anyway.
 */
function AuthenticatedArea({ router }: Readonly<{ router: AppRouter }>): ReactNode {
  const { state } = useSessionContext();

  if (state.status === 'anonymous') {
    return <LoginPage />;
  }

  const { gate, user } = state.session;
  const behindAnOverlay = state.status === 'locked' || state.status === 'expired';

  if (!behindAnOverlay) {
    if (gate === 'password_change') {
      return <PasswordChangePage />;
    }
    if (gate === 'totp_enrolment') {
      return <TotpEnrolmentPage />;
    }
    if (!user.hasPin) {
      return <PinSetupPage />;
    }
  }

  return (
    <>
      <RouterProvider router={router} />
      <ReauthenticationOverlay />
    </>
  );
}
